/**
 * Flight API Hooks
 *
 * React Query hooks for flight CRUD and control operations
 * Based on Mavlink API flight-controller endpoints
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api-client';
import { flightQueryKeys } from './flight.keys';
import type {
    FlightResponse,
    FlightCreateRequest,
    FlightListFilters,
    PageFlightResponse,
    TelemetryResponse,
    TelemetryQueryParams,
    GeoJsonResponse,
    BatteryDataResponse,
} from '../types/flight.types';

// ============================================================================
// QUERIES
// ============================================================================

/**
 * List flights with optional filtering and pagination
 */
export function useFlightsQuery(filters?: FlightListFilters) {
    return useQuery({
        queryKey: flightQueryKeys.list(filters),
        queryFn: () =>
            apiGet<PageFlightResponse>('/api/flights', {
                params: {
                    page: filters?.page ?? 0,
                    size: filters?.size ?? 20,
                    ...(filters?.status && { status: filters.status }),
                    ...(filters?.missionId && { missionId: filters.missionId }),
                },
            }),
        staleTime: 30 * 1000, // 30 seconds
    });
}

/**
 * Get a single flight by ID
 */
export function useFlightQuery(id: string | null) {
    return useQuery({
        queryKey: flightQueryKeys.detail(id || ''),
        queryFn: () => {
            if (!id) throw new Error('Flight ID is required');
            return apiGet<FlightResponse>(`/api/flights/${id}`);
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

/**
 * Get the currently active flight
 */
export function useActiveFlightQuery() {
    return useQuery({
        queryKey: flightQueryKeys.active(),
        queryFn: () => apiGet<FlightResponse>('/api/flights/active'),
        staleTime: 10 * 1000, // 10 seconds - refresh frequently for active flight
        retry: false, // Don't retry if no active flight
    });
}

/**
 * Get telemetry data for a flight
 */
export function useTelemetryQuery(id: string | null, params?: TelemetryQueryParams) {
    return useQuery({
        queryKey: flightQueryKeys.telemetry(id || '', params),
        queryFn: () => {
            if (!id) throw new Error('Flight ID is required');
            return apiGet<TelemetryResponse[]>(`/api/flights/${id}/telemetry`, {
                params: {
                    ...(params?.from && { from: params.from }),
                    ...(params?.to && { to: params.to }),
                },
            });
        },
        enabled: !!id,
        staleTime: 5 * 1000, // 5 seconds - telemetry updates frequently
    });
}

/**
 * Get flight path as GeoJSON
 */
export function useFlightPathQuery(id: string | null) {
    return useQuery({
        queryKey: flightQueryKeys.path(id || ''),
        queryFn: () => {
            if (!id) throw new Error('Flight ID is required');
            return apiGet<GeoJsonResponse>(`/api/flights/${id}/path`);
        },
        enabled: !!id,
        staleTime: 30 * 1000, // 30 seconds
    });
}

/**
 * Get battery history for a flight
 */
export function useBatteryHistoryQuery(id: string | null) {
    return useQuery({
        queryKey: flightQueryKeys.battery(id || ''),
        queryFn: () => {
            if (!id) throw new Error('Flight ID is required');
            return apiGet<BatteryDataResponse[]>(`/api/flights/${id}/battery`);
        },
        enabled: !!id,
        staleTime: 10 * 1000, // 10 seconds
    });
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create a new flight
 */
export function useCreateFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: FlightCreateRequest) =>
            apiPost<FlightResponse>('/api/flights', data),
        onSuccess: () => {
            // Invalidate flights list
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Create failed:', error);
        },
    });
}

/**
 * Start a flight
 */
export function useStartFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            apiPost<FlightResponse>(`/api/flights/${id}/start`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.detail(data.flightId),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.active(),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Start failed:', error);
        },
    });
}

/**
 * Pause a flight
 */
export function usePauseFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            apiPost<FlightResponse>(`/api/flights/${id}/pause`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.detail(data.flightId),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.active(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Pause failed:', error);
        },
    });
}

/**
 * Resume a paused flight
 */
export function useResumeFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            apiPost<FlightResponse>(`/api/flights/${id}/resume`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.detail(data.flightId),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.active(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Resume failed:', error);
        },
    });
}

/**
 * Complete a flight
 */
export function useCompleteFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            apiPost<FlightResponse>(`/api/flights/${id}/complete`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.detail(data.flightId),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.active(),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Complete failed:', error);
        },
    });
}

/**
 * Abort a flight
 */
export function useAbortFlightMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            apiPost<FlightResponse>(`/api/flights/${id}/abort`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.detail(data.flightId),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.active(),
            });
            queryClient.invalidateQueries({
                queryKey: flightQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Flight API] Abort failed:', error);
        },
    });
}
